/**
 * What a document surface needs that it cannot see, derived from the objects
 * the lens is about to show.
 *
 * Its own module rather than a corner of `main.ts`, and the reason is a
 * defect: the note derivation was written under a guard that belonged to the
 * contents list — `if (!nameFrom) return undefined` — so a pool with prose and
 * citations but no title produced no apparatus at all, for a reason that had
 * nothing to do with notes. @Codex found it by reading. Nothing could have
 * found it by running, because none of this was reachable from a test: it lived
 * inside a 2700-line module that mounts a page on import.
 *
 * So the rule this file exists to keep is that the HOST's derivations are
 * checkable. `apps/demo/test/document-host.test.ts` is what checks them.
 */
import {
  sectionAnchor, noteAnchor, documentSectionTemplate, labelForRole,
  type BoundValues, type IR, type CapabilityInputs, type DocumentNote,
} from '@morph/runtime';
import { ROLES, ROLE_KIND, type Role } from '@morph/spec';

/**
 * The contents entries, anchored with the SAME function the card renderer uses.
 *
 * One implementation of the anchor on both sides is the whole design: a link
 * computed here and a target emitted there is two computations that must
 * agree, which is the shape of four separate defects in this repo. The browser
 * gate asserts the property directly as well — every href must resolve to an
 * element that exists — because a shared function is an argument and a
 * resolving link is evidence.
 */
export function documentSections(
  ir: IR, objects: readonly BoundValues[], labels: Partial<Record<Role, string>>,
): CapabilityInputs | undefined {
  // The DOCUMENT-FLOW node, not the first capability: under a named look
  // (Word) the title bar and the ribbon come first in the IR, and the
  // contents fed to the title bar is a contents nobody draws.
  const capAt = ir.nodes.findIndex((n) => n.kind === 'capability' && n.capability === 'document-flow');
  if (capAt < 0) return undefined;
  // The collection INSIDE the document, resolved by the runtime rather than
  // guessed at here. This used to be `ir.nodes.find((n) => n.kind ===
  // 'collection')` — the first collection anywhere in the IR — and `withShell`
  // puts a panel with its own collection ahead of the document body, so every
  // contents entry was minted from the wrong node and pointed at a card in the
  // shell's list. Sharing `sectionAnchor` was not enough; the node index is
  // half the anchor.
  const tpl = documentSectionTemplate(ir, capAt);
  if (tpl === undefined) return undefined;
  // A missing `nameFrom` is a document whose sections have no title, not a
  // document with no sections. This used to `return undefined` here, which was
  // defensible while the only thing downstream was a contents list — and became
  // wrong the moment the notes were derived below it: a pool with prose and
  // citations but no title role rendered sections, rendered "Section 1" on each
  // fold, and produced no apparatus and no markers at all, because the
  // apparatus had been made to depend on a question it never asked. Found by
  // @Codex. The fallback name was already written one line down; only the guard
  // above it had to go.
  const nameFrom = ir.nodes[tpl]?.nameFrom;
  const sections = objects.map((o, k) => ({
    anchor: sectionAnchor(tpl, k),
    name: String((nameFrom ? o[nameFrom] : undefined) ?? `Section ${k + 1}`),
  }));
  const notes = documentNotes(tpl, objects, labels);
  return new Map([[capAt, {
    capability: 'document-flow' as const,
    document: { sections, ...(notes.length ? { notes } : {}) },
  }]]);
}

/**
 * The footnotes, DERIVED from what the sections actually cite.
 *
 * The Notes pool's own description is *"notes that cite each other. A vault."*
 * — so the apparatus was already in the data and had never been drawn. A
 * footnote is not a second field somebody types beside the prose; it is the
 * citation, written where an apparatus goes. Nothing here is authored: no note
 * text lives in this repo, and a pool whose objects reference nothing produces
 * no notes at all rather than an empty rule above nothing.
 *
 * **One note per section, marked at the section.** Word puts the marker at a
 * character position inside a run of text, and that is not reachable from here:
 * a prose value is one opaque string to the store, and a marker inside it needs
 * the `text-editing` capability, which is not built. Saying so is the point —
 * per-section is the honest version of this, not a stand-in that is quietly
 * described as the other thing. `ACCEPTANCE.md` carries the same sentence.
 *
 * Which reference becomes the note is decided in vocabulary order rather than
 * by whatever order the projection happened to put the roles in.
 *
 * ONE ROLE FOR THE WHOLE DOCUMENT, chosen before the loop rather than inside
 * it. The comment here used to claim that vocabulary ordering already gave
 * that, and @Codex read it and pointed out that it does not: a `find` per
 * object makes each individual choice deterministic and says nothing about
 * whether two objects choose the same way. A pool where some sections carry
 * `relation.peer` and others carry `relation.parent` produced an apparatus that
 * changed what it was about halfway down — the exact failure the comment
 * claimed to have prevented.
 *
 * So the role is picked once, from the first in vocabulary order that ANY
 * section populates, and a section that does not carry it gets no note. An
 * apparatus is about one kind of relationship or it is a list of unrelated
 * facts with numbers on them.
 */
export function documentNotes(
  tpl: number, objects: readonly BoundValues[], labels: Partial<Record<Role, string>>,
): DocumentNote[] {
  const role = ROLES.find((r) =>
    ROLE_KIND[r] === 'ref' && objects.some((o) => cited(o[r]).length > 0));
  if (!role) return [];
  const out: DocumentNote[] = [];
  objects.forEach((o, k) => {
    const names = cited(o[role]).map(citedName).filter((n): n is string => n !== undefined);
    if (!names.length) return;
    out.push({
      anchor: sectionAnchor(tpl, k),
      note: noteAnchor(tpl, k),
      // The host's own word for the role, not the vocabulary's: the Notes pool
      // declares `relation.peer` and calls it **Links to**, and an apparatus
      // that said "Relation peer" would be describing the schema to a reader.
      //
      // Quoted and joined rather than colon-separated, which is @Codex's note
      // that `Links to: A, B` reads as a UI label where a footnote wants prose.
      // The suggested `See "A" and "B"` reads better still and was not taken:
      // "See" is a word this repo would be INVENTING, while the label is a word
      // the pool declared — and it carries information a generic verb throws
      // away, since the Tasks pool calls its reference **Background** and the
      // Films pool calls its **Filmmaker**. So the punctuation changed and the
      // vocabulary did not.
      text: `${labels[role] ?? labelForRole(role)} ${citation(names)}`,
    });
  });
  return out;
}

/**
 * The cited names as one phrase: quoted, and joined the way a sentence joins
 * them rather than the way an array prints.
 *
 * `A, B` is a list; `"A" and "B"` is prose, and the quotes are what keep a
 * two-word title from reading as two citations.
 */
const citation = (names: readonly string[]): string => {
  const quoted = names.map((n) => `\u201c${n}\u201d`);
  if (quoted.length <= 1) return quoted[0] ?? '';
  return `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
};

/** A ref role's value, as the objects it points at — the projection expands one
 *  level, so a citation arrives as the cited object rather than as an id. */
export const cited = (v: unknown): readonly BoundValues[] =>
  Array.isArray(v) ? (v as BoundValues[])
    : v && typeof v === 'object' ? [v as BoundValues] : [];

/**
 * What a cited object is CALLED.
 *
 * `transcript` and `body` are excluded for the reason the reference preview
 * excludes the first: they are long prose that belongs to an object, never the
 * object's identity, and a note reading ten thousand characters of transcript
 * is an apparatus that ate the document. Found once already, by @Codex, one
 * layer down.
 *
 * Deliberately not `titleOf` from `panes.ts`, which answers a different
 * question: a pane header must say SOMETHING, so it falls through to the word
 * "Object". A note is a claim about what was cited, and "Links to: Object" is a
 * claim nobody made — an unnameable citation drops out of the apparatus
 * instead.
 */
export function citedName(target: BoundValues): string | undefined {
  const role = (Object.keys(target) as Role[]).find((r) => r === 'title' || r === 'subtitle')
    ?? (Object.keys(target) as Role[]).find((r) =>
      ROLE_KIND[r] === 'text' && r !== 'transcript' && r !== 'body');
  const v = role ? target[role] : undefined;
  return typeof v === 'string' && v.trim() ? v : undefined;
}
